import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { X, UserPlus, MagnifyingGlass } from '@phosphor-icons/react';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';

const MAX = 4;

// Turns an existing court booking into an open match — the court stays reserved.
export const OpenMatchFromBooking = ({ booking, open, onClose, onDone }) => {
  const { language, API, user } = useAuth();
  const L = (en, de) => (language === 'de' ? de : en);
  const isStaff = user.role !== 'customer';
  const [q, setQ] = useState('');
  const [hits, setHits] = useState([]);
  const [players, setPlayers] = useState([]);
  const [guest, setGuest] = useState('');
  const [guests, setGuests] = useState([]);
  const [minL, setMinL] = useState('1.0');
  const [maxL, setMaxL] = useState('7.9');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const bookerCounts = booking?.user_role ? booking.user_role === 'customer' : !isStaff || booking?.user_id !== user.id;
  const used = (bookerCounts ? 1 : 0) + players.length + guests.length;
  const full = used >= MAX - 1;

  useEffect(() => {
    if (q.length < 2) { setHits([]); return; }
    const t = setTimeout(async () => {
      try {
        const url = isStaff ? `/users/search?q=${encodeURIComponent(q)}&limit=8` : `/users/partner-search?q=${encodeURIComponent(q)}&limit=8`;
        const { data } = await API.get(url);
        setHits(data.filter(u => u.id !== booking.user_id && !players.some(p => p.id === u.id)));
      } catch { setHits([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, API, isStaff, booking, players]);

  const addGuest = () => { if (guest.trim() && !full) { setGuests([...guests, guest.trim()]); setGuest(''); } };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await API.post(`/bookings/${booking.id}/open-match`, {
        player_ids: players.map(p => p.id), guest_names: guests,
        min_level: parseFloat(minL), max_level: parseFloat(maxL), notes: notes || null,
      });
      toast.success(L(`Open match created — ${data.free_spots} spot(s) free`, `Open Match erstellt — ${data.free_spots} Platz/Plätze frei`));
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Could not create open match', 'Open Match konnte nicht erstellt werden'));
    } finally { setSaving(false); }
  };

  const chip = (label, onRemove, tid) => (
    <span key={tid} className="inline-flex items-center gap-1 bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm rounded-full px-3 py-1" data-testid={tid}>
      {label}
      {onRemove && <button type="button" onClick={onRemove} className="hover:text-red-600"><X size={12} /></button>}
    </span>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md max-h-[90vh] overflow-y-auto" data-testid="open-match-from-booking-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">{L('Open as open match', 'Als Open Match öffnen')}</DialogTitle>
          <DialogDescription>
            {booking && `${booking.court_name} · ${booking.date} · ${booking.start_time}–${booking.end_time}. `}
            {L('The court stays reserved; others can join the free spots.', 'Der Platz bleibt reserviert; andere können die freien Plätze buchen.')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="label-text">{L('Already playing', 'Spielen schon mit')} ({used}/{MAX})</Label>
            <div className="flex flex-wrap gap-2">
              {bookerCounts && chip(booking?.user_name || L('Booker', 'Bucher'), null, 'omb-booker-chip')}
              {players.map(p => chip(p.name, () => setPlayers(players.filter(x => x.id !== p.id)), `omb-player-${p.id}`))}
              {guests.map((g, i) => chip(`${g} (${L('guest', 'Gast')})`, () => setGuests(guests.filter((_, j) => j !== i)), `omb-guest-${i}`))}
            </div>
          </div>

          {!full && (
            <>
              <div className="relative">
                <MagnifyingGlass size={16} className="absolute left-3 top-3 text-slate-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="omb-search-input"
                  placeholder={L('Search player by name', 'Spieler suchen (Name)')} />
                {hits.length > 0 && (
                  <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {hits.map(u => (
                      <button key={u.id} type="button" data-testid={`omb-hit-${u.id}`}
                        onClick={() => { setPlayers([...players, u]); setQ(''); setHits([]); }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50">
                        {u.name} {u.player_level != null && <span className="text-slate-400">· L{u.player_level}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Input value={guest} onChange={(e) => setGuest(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addGuest()}
                  placeholder={L('Guest without account (name)', 'Gast ohne Konto (Name)')} data-testid="omb-guest-input" />
                <Button type="button" variant="outline" onClick={addGuest} data-testid="omb-guest-add-btn"><UserPlus size={16} /></Button>
              </div>
            </>
          )}
          {full && <p className="text-xs text-amber-700">{L('At least one spot must stay free.', 'Mindestens ein Platz muss frei bleiben.')}</p>}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="label-text">{L('Min level', 'Min. Level')}</Label>
              <Input type="number" step="0.1" min="1" max="7.9" value={minL} onChange={(e) => setMinL(e.target.value)} data-testid="omb-min-level" />
            </div>
            <div className="space-y-1">
              <Label className="label-text">{L('Max level', 'Max. Level')}</Label>
              <Input type="number" step="0.1" min="1" max="7.9" value={maxL} onChange={(e) => setMaxL(e.target.value)} data-testid="omb-max-level" />
            </div>
          </div>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={L('Note (optional)', 'Notiz (optional)')} data-testid="omb-notes-input" />
          <p className="text-xs text-slate-500">{L('New players confirm by paying their share online or at the club.', 'Neue Spieler bestätigen, indem sie ihren Anteil online oder im Club bezahlen.')}</p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="flex-1">{L('Cancel', 'Abbrechen')}</Button>
            <Button onClick={save} disabled={saving || used === 0} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold" data-testid="omb-save-btn">
              {saving ? '…' : L('Open match', 'Match öffnen')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
